/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { getAdminApiUrl } from "utils/auth";

// react-chartjs-2 + chart.js
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EMPTY_STATS = {
  labels: [],
  subscription: [],
  totalUsers: [],
  activeUsers: [],
};

const EMPTY_STAT_CARDS = {
  subscriptionChange: "-",
  totalUsersChange: "-",
  totalUsersCount: "-",
  totalActiveUsers: "-",
};

function Dashboard() {
  const formatChange = React.useCallback((previous, current) => {
    if (typeof previous !== "number" || typeof current !== "number") return "0%";
    if (previous === 0) return current === 0 ? "0%" : "+100%";
    const delta = ((current - previous) / previous) * 100;
    const sign = delta > 0 ? "+" : "";
    return `${sign}${delta.toFixed(1)}%`;
  }, []);

  const crosshairPlugin = React.useMemo(
    () => ({
      id: "crosshairLine",
      afterDraw: (chart) => {
        const tooltip = chart.tooltip;
        const active = tooltip?.getActiveElements?.() || [];
        if (!active.length) return;

        const { ctx, chartArea } = chart;
        const x = active[0].element.x;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(229, 10, 70, 0.45)";
        ctx.stroke();
        ctx.restore();
      },
    }),
    []
  );

  const [range, setRange] = React.useState("daily");
  const [dashboardStats, setDashboardStats] = React.useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = React.useState(true);
  const [dashboardError, setDashboardError] = React.useState("");
  const [statCards, setStatCards] = React.useState(EMPTY_STAT_CARDS);

  const handleRange = (_event, nextRange) => {
    if (nextRange) setRange(nextRange);
  };

  React.useEffect(() => {
    const fetchDashboardStats = async () => {
      setIsDashboardLoading(true);
      setDashboardError("");
      const apiBaseUrl = getAdminApiUrl();
      const periodLimit = range === "daily" ? 30 : 12;
      const endpoint = `/dashboard/statistics?period=${encodeURIComponent(
        range
      )}&limit=${periodLimit}`;

      try {
        const response = await axios.get(`${apiBaseUrl}${endpoint}`);

        const payload = response?.data?.data || response?.data || {};
        const trends = Array.isArray(payload.trends) ? payload.trends : [];
        const totals = payload.totals && typeof payload.totals === "object" ? payload.totals : null;

        const labelsFromTrends = trends
          .map((item) => {
            if (!item?.period) return "";
            const date = new Date(item.period);
            if (Number.isNaN(date.getTime())) return String(item.period);
            return new Intl.DateTimeFormat("en-GB", {
              day: "2-digit",
              month: "short",
            }).format(date);
          })
          .filter(Boolean);

        const subscriptionFromTrends = trends.map((item) => {
          const total = Number(item?.totalUsers || 0);
          const converted = Number(item?.convertedUsers || 0);
          if (!total) return 0;
          return Number(((converted / total) * 100).toFixed(2));
        });

        const totalUsersFromTrends = trends
          .map((item) => Number(item?.totalUsers))
          .filter((value) => Number.isFinite(value));

        const activeUsersFromTrends = trends
          .map((item) => Number(item?.activeUsers))
          .filter((value) => Number.isFinite(value));

        const labels = Array.isArray(payload.labels)
          ? payload.labels
          : Array.isArray(payload.chart?.labels)
          ? payload.chart.labels
          : labelsFromTrends;

        let subscription = Array.isArray(payload.subscription)
          ? payload.subscription
          : Array.isArray(payload.subscriptionSeries)
          ? payload.subscriptionSeries
          : Array.isArray(payload.subscription?.series)
          ? payload.subscription.series
          : subscriptionFromTrends;

        if (subscription.length <= 1 && totals) {
          const conversionRate = Number(totals.subscriptionConversionRate);
          if (Number.isFinite(conversionRate)) {
            subscription = [conversionRate];
          }
        }

        const totalUsers = Array.isArray(payload.totalUsers)
          ? payload.totalUsers
          : Array.isArray(payload.totalUsersSeries)
          ? payload.totalUsersSeries
          : Array.isArray(payload.users?.totalSeries)
          ? payload.users.totalSeries
          : totalUsersFromTrends;

        const activeUsers = Array.isArray(payload.activeUsers)
          ? payload.activeUsers
          : Array.isArray(payload.activeUsersSeries)
          ? payload.activeUsersSeries
          : Array.isArray(payload.users?.activeSeries)
          ? payload.users.activeSeries
          : activeUsersFromTrends;

        setDashboardStats({
          labels,
          subscription,
          totalUsers,
          activeUsers,
        });

        setStatCards({
          subscriptionChange: totals
            ? `${Number(totals.subscriptionConversionRate || 0).toFixed(2)}%`
            : payload.subscriptionChange || payload.subscription?.change || "-",

          totalUsersChange:
            trends.length >= 2
              ? formatChange(
                  Number(trends[trends.length - 2]?.totalUsers || 0),
                  Number(trends[trends.length - 1]?.totalUsers || 0)
                )
              : payload.totalUsersChange || payload.users?.change || "-",

          totalUsersCount:
            totals?.totalUsers ?? payload.totalUsersCount ?? payload.users?.totalCount ?? "-",

          totalActiveUsers:
            totals?.totalActiveUsers ??
            payload.totalActiveUsersCount ??
            payload.activeUsers?.totalCount ??
            (activeUsersFromTrends.length
              ? activeUsersFromTrends[activeUsersFromTrends.length - 1]
              : "-"),
        });
      } catch (error) {
        setDashboardStats(EMPTY_STATS);
        setStatCards(EMPTY_STAT_CARDS);
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to load dashboard statistics.";
        setDashboardError(apiMessage);
      } finally {
        setIsDashboardLoading(false);
      }
    };

    fetchDashboardStats();
  }, [formatChange, range]);

  const chartsByRange = React.useMemo(() => dashboardStats || EMPTY_STATS, [dashboardStats]);

  const makeLineChart = React.useCallback((labels, { label, data }, options = {}) => {
    const safeLabels = Array.isArray(labels)
      ? labels.filter((item) => item !== undefined && item !== null)
      : [];
    const safeData = Array.isArray(data)
      ? data.map((value) => (Number.isFinite(Number(value)) ? Number(value) : 0))
      : [];
    const baseLabels = safeLabels.length ? safeLabels : ["No data"];
    const isSinglePointSeries = baseLabels.length === 1 && safeData.length === 1;
    const chartLabels = isSinglePointSeries ? ["", baseLabels[0]] : baseLabels;
    let chartData = safeData.slice(0, chartLabels.length);

    if (!chartData.length) {
      chartData = Array(chartLabels.length).fill(0);
    } else if (chartData.length === 1 && chartLabels.length > 1) {
      // For single API point, show trend from 0 to actual value.
      chartData = [0, chartData[0]];
    } else if (chartData.length < chartLabels.length) {
      const lastValue = chartData[chartData.length - 1];
      chartData = [...chartData, ...Array(chartLabels.length - chartData.length).fill(lastValue)];
    }

    const allZero = chartData.every((value) => Number(value) === 0);

    return {
      data: {
        labels: chartLabels,
        datasets: [
          {
            label,
            data: chartData,
            tension: 0.4,
            pointRadius: 2.5,
            pointHoverRadius: 4,
            pointHitRadius: 12,
            pointBackgroundColor: "rgba(229, 10, 70, 1)",
            pointBorderColor: "rgba(229, 10, 70, 1)",
            pointBorderWidth: 2,
            borderWidth: 3,
            borderColor: "rgba(229, 10, 70, 1)",
            backgroundColor: (context) => {
              const { chart } = context;
              const { ctx, chartArea } = chart;

              if (!chartArea) return "transparent";

              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, "rgba(229, 10, 70, 0.45)");
              gradient.addColorStop(1, "rgba(229, 10, 70, 0.00)");

              return gradient;
            },
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        scales: {
          y: {
            grid: { drawBorder: false, color: "rgba(0,0,0,0.04)" },
            beginAtZero: !!options.beginAtZero,
            min: options.beginAtZero ? 0 : undefined,
            suggestedMax: options.beginAtZero && allZero ? 1 : undefined,
            max: Number.isFinite(options.max) ? options.max : undefined,
            ticks: {
              color: "#67748e",
              padding: 8,
              font: { size: 12, family: "Roboto" },
              maxTicksLimit: 5,
            },
          },
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { color: "#67748e", padding: 8, font: { size: 12, family: "Roboto" } },
          },
        },
        elements: {
          line: { capBezierPoints: true },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
      },
    };
  }, []);

  const subscriptionChart = React.useMemo(
    () =>
      makeLineChart(
        chartsByRange.labels,
        { label: "Subscription", data: chartsByRange.subscription },
        { beginAtZero: true, max: 100 }
      ),
    [chartsByRange, makeLineChart]
  );

  const totalUsersChart = React.useMemo(
    () =>
      makeLineChart(
        chartsByRange.labels,
        { label: "Total Users", data: chartsByRange.totalUsers },
        "rgba(229, 10, 70, 1)"
      ),
    [chartsByRange, makeLineChart]
  );

  const activeUsersChart = React.useMemo(
    () =>
      makeLineChart(
        chartsByRange.labels,
        { label: "Active Users", data: chartsByRange.activeUsers },
        "rgba(229, 10, 70, 1)"
      ),
    [chartsByRange, makeLineChart]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="flex-start" mb={2}>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={handleRange}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                border: 0,
                px: 2,
                py: 0.75,
                borderRadius: "0.5rem !important",
                mr: 1,
                backgroundColor: "#9e9e9e",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#8a8a8a",
                  color: "#fff",
                },
              },
              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: "rgba(229, 10, 70, 1)",
                color: "#fff",
              },
              "& .MuiToggleButton-root.Mui-selected:hover": {
                backgroundColor: "rgba(229, 10, 70, 0.9)",
              },
            }}
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly">Yearly</ToggleButton>
          </ToggleButtonGroup>
        </MDBox>

        {dashboardError && (
          <MDBox mb={2}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="button" color="error">
                  {dashboardError}
                </MDTypography>
              </MDBox>
            </Card>
          </MDBox>
        )}

        {isDashboardLoading ? (
          <Card>
            <MDBox py={8} display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CircularProgress size={20} sx={{ color: "rgba(229, 10, 70, 1)" }} />
              <MDTypography variant="button" color="text">
                Loading latest dashboard data...
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ overflow: "visible" }}>
                <MDBox p={2}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDTypography variant="h6" fontWeight="bold">
                      Subscription{" "}
                      <MDTypography component="span" variant="h6" fontWeight="bold">
                        Conversion Rate
                      </MDTypography>
                    </MDTypography>
                    <MDTypography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "rgba(229, 10, 70, 1)" }}
                    >
                      {statCards.subscriptionChange}
                    </MDTypography>
                  </MDBox>
                  <MDBox mt={2} height={{ xs: "12rem", md: "14rem" }}>
                    <Line
                      data={subscriptionChart.data}
                      options={subscriptionChart.options}
                      plugins={[crosshairPlugin]}
                      redraw
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <MDBox p={2} height="100%" display="flex" flexDirection="column">
                  <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDTypography variant="button" fontWeight="bold" color="text">
                      Total Users
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="h4" fontWeight="bold" mt={1}>
                    {statCards.totalUsersCount}
                  </MDTypography>
                  <MDBox mt="auto" height="9rem">
                    <Line
                      data={totalUsersChart.data}
                      options={totalUsersChart.options}
                      plugins={[crosshairPlugin]}
                      redraw
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <MDBox p={2} height="100%" display="flex" flexDirection="column">
                  <MDTypography variant="button" fontWeight="bold" color="text">
                    Active Users
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold" mt={1}>
                    {statCards.totalActiveUsers}
                  </MDTypography>
                  <MDBox mt="auto" height="9rem">
                    <Line
                      data={activeUsersChart.data}
                      options={activeUsersChart.options}
                      plugins={[crosshairPlugin]}
                      redraw
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
